import React from 'react';
import GeneNetworkGraph from '../GeneNetworkGraph';
import { enhanceLollipopHtml } from './lollipopEnhancer';

// --- utils ---
const getExact = (files, name) => files.find(f => f.name === name)?.data || '';
const findRx = (files, rx) => files.find(f => rx.test(f?.name || ''));

const extractXQTL = (txt, prefix) => {
  if (!txt) return '';
  const lines = txt.trim().split('\n'); if (!lines.length) return '';
  const hdr = lines[0].split('\t');
  const keep = hdr.map((h,i)=>[h,i]).filter(([h])=>h.startsWith(prefix));
  if (!keep.length) return '';
  const head = keep.map(([h]) => h.replace(prefix+'.','')).join('\t');
  const body = lines.slice(1).map(row=>{
    const c = row.split('\t'); return keep.map(([,i])=>c[i]||'').join('\t');
  });
  return [head, ...body].join('\n');
};

const inferGene = (files, fallback='') => {
  const cnt = {};
  files.forEach(f=>{
    const m=(f.name||'').match(/([^/\\]+?)-(clinvar|clingen|gencc|dgidb|eqtl_.*|sqtl_.*|pqtl_.*|network)\.txt$/i);
    if (m) cnt[m[1]]=(cnt[m[1]]||0)+1;
  });
  return Object.entries(cnt).sort((a,b)=>b[1]-a[1])[0]?.[0] || fallback;
};

const textDataURI = (t, m='text/plain') => `data:${m};charset=utf-8,${encodeURIComponent(t||'')}`;
const base64DataURI = (b64, m='application/octet-stream') => `data:${m};base64,${(b64||'').trim()}`;

const getMime = (name='') => {
  const n = name.toLowerCase();
  if (n.endsWith('.js')) return 'text/javascript';
  if (n.endsWith('.css')) return 'text/css';
  if (n.endsWith('.json')) return 'application/json';
  if (n.endsWith('.svg')) return 'image/svg+xml';
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.gif')) return 'image/gif';
  return 'application/octet-stream';
};

const inlineHtmlAssets = (htmlString, files, htmlFileName) => {
  if (!htmlString) return '';
  const idx = {};
  (files || []).forEach((f) => {
    if (!f?.name) return;
    const key = f.name.replace(/^\.?\/*/, '').replace(/\/+/g, '/');
    idx[key] = f;
  });

  const resolve = (url) => {
    if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;
    const clean = url.replace(/^\.?\/*/, '').replace(/\/+/g, '/');
    const base = (htmlFileName || '').replace(/\.html?$/i, '') + '_files/';
    const f = idx[clean] || idx[(base + clean).replace(/\/+/g, '/')];
    if (!f) return url;
    const mime = getMime(f.name);
    if (mime.startsWith('image/')) {
      // assume binary already base64-ish if not clearly plain text
      const looksB64 = /^[A-Za-z0-9+/=\s]+$/.test(f.data || '');
      return looksB64 ? base64DataURI(f.data, mime) : textDataURI(f.data, mime);
    }
    return textDataURI(f.data || '', mime);
  };

  let inlined = htmlString;
  inlined = inlined.replace(/(src|href)\s*=\s*"(.*?)"/gi, (_m, a, u) => `${a}="${resolve(u)}"`);
  inlined = inlined.replace(/(src|href)\s*=\s*'(.*?)'/gi, (_m, a, u) => `${a}="${resolve(u)}"`);
  return inlined;
};

// --- special tab renderers ---
const LollipopRenderer = ({ section, files }) => {
  const htmlObj = section?.lollipop?.html;
  const jsonObj = section?.lollipop?.json;
  const all = section?.lollipop?.all || files;

  if (htmlObj?.data) {
    const inlined = inlineHtmlAssets(htmlObj.data, all, htmlObj.name);
    const enhanced = enhanceLollipopHtml(inlined);
    return (
      <div className="WSNG-lollipop-container">
        <iframe
          title="ClinVar Lollipop"
          className="WSNG-lollipop-iframe"
          style={{ width: '100%', minHeight: 620, border: 0 }}
          srcDoc={enhanced}
        />
      </div>
    );
  }
  if (jsonObj?.data) {
    return (
      <div className="WSNG-json-fallback">
        <div className="WSNG-note">No HTML widget found; showing JSON payload.</div>
        <pre className="WSNG-pre">{jsonObj.data}</pre>
      </div>
    );
  }
  return <div className="WSNG-no-analysis">No ClinVar lollipop available.</div>;
};

const NetworkRenderer = ({ section, activeSub }) => (
  <GeneNetworkGraph data={(section.tables || []).find((t) => t.name === activeSub)?.data || ''} />
);

// --- organizer (gene only) ---
export const organizeGene = (files, jobData) => {
  // Accept old gene_output.* and new <GENE>-*.txt
  const clingen = getExact(files, 'gene_output.clingen.txt') || findRx(files, /-clingen\.txt$/i)?.data || '';
  const clinvar = getExact(files, 'gene_output.clinvar.txt') || findRx(files, /-clinvar\.txt$/i)?.data || '';
  const gencc   = getExact(files, 'gene_output.genecc.txt')  || findRx(files, /-gencc\.txt$/i)?.data   || '';
  const dgidb   = findRx(files, /-dgidb\.txt$/i)?.data || '';

  const xqtlCollapsed = getExact(files, 'gene_output.xqtl.txt') || '';
  const eqtl_catalog = extractXQTL(xqtlCollapsed, 'eQTL_Catalog') || findRx(files, /-eqtl_catalog\.txt$/i)?.data || '';
  const eqtl_gtex    = extractXQTL(xqtlCollapsed, 'eQTL_GTEx')   || findRx(files, /-eqtl_gtex\.txt$/i)?.data    || '';
  const eqtl_mr      = extractXQTL(xqtlCollapsed, 'eQTL_MR')     || findRx(files, /-eqtlmr\.txt$/i)?.data        || '';
  const pqtl_epi     = extractXQTL(xqtlCollapsed, 'pQTL')        || findRx(files, /-pqtl_epigraphdb\.txt$/i)?.data || '';
  const sqtl_gtex    = extractXQTL(xqtlCollapsed, 'sQTL_GTEx')   || findRx(files, /-sqtl_gtex\.txt$/i)?.data     || '';

  const networkTxt = getExact(files, 'gene_output.network.txt') || findRx(files, /-network\.txt$/i)?.data || '';

  const lolliHtml = findRx(files, /ClinVarPLP-Lollipop-.*\.html$/i) || null;
  const lolliJson = findRx(files, /ClinVarPLP-Lollipop-.*\.json$/i) || null;

  const gene = inferGene(files, (jobData?.input_text || jobData?.original_files || '').trim());

  const externalLinks = [
    gene && { label: 'Open GTEx (Expression)', url: `https://gtexportal.org/home/gene/${encodeURIComponent(gene)}` },
    gene && { label: 'Open Human Protein Atlas', url: `https://www.proteinatlas.org/search/${encodeURIComponent(gene)}` },
    gene && { label: 'PubMed (Gene)', url: `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(gene)}+AND+human` },
  ].filter(Boolean);

  const organized = {
    'Clinical Evidence': {
      tables: [
        { name: 'ClinGen', data: clingen, active: true },
        { name: 'ClinVar', data: clinvar },
        { name: 'GenCC',   data: gencc },
      ]
    },
    'ClinVar Lollipop': {
      lollipop: { html: lolliHtml, json: lolliJson, all: files }
    },
    ...(networkTxt ? {
      'Network': { tables: [{ name: 'Interactions', data: networkTxt, active: true }] }
    } : {}),
    'xQTL & Gene-Drug': {
      tables: [
        { name: 'eQTL Catalog',       data: eqtl_catalog, active: true },
        { name: 'eQTL GTEx',          data: eqtl_gtex },
        { name: 'eQTL MR',            data: eqtl_mr },
        { name: 'pQTL (EpigraphDB)',  data: pqtl_epi },
        { name: 'sQTL GTEx',          data: sqtl_gtex },
        { name: 'DGIdb',              data: dgidb },
      ]
    }
  };

  const renderers = {
    'ClinVar Lollipop': (props) => <LollipopRenderer {...props} />,
    'Network': (props) => <NetworkRenderer {...props} />,
  };

  const initial = { tab: 'Clinical Evidence', sub: 'ClinGen' };
  return { organized, renderers, initial, externalLinks };
};
